import { useEffect, useMemo, useState } from "react";
import { m } from "framer-motion";
import { useForm, useWatch, Controller } from "react-hook-form";
import { Box } from "@storyofams/react-ui";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { Button, CenteredTitle } from "~/components";
import config from "~/config";
import { FlowContainerFragmentFragment } from "~/graphql/api/sdk";
import { useFlow, useIsMobile, useSession } from "~/hooks";
import { showToast } from "~/lib";
import { replaceVariables } from "~/context/helper";
import Slider from "rc-slider";

import { messages } from "./messages";
import QuestionContentBlocksList from "~/components/QuestionContentBlocksList";

interface QuestionFormProps {
  current: number;
  question: FlowContainerFragmentFragment["flow"]["nodes"][0];
  isLastQuestion: boolean;
}

const isExactMatch = (ruleArray, answersArray) => {
  // true if all elements in ruleArray are in answersArray and vice versa
  return ruleArray.length === answersArray.length && ruleArray.every((value) => answersArray.includes(value));
};

const isOpenMatch = (ruleArray, answersArray) => {
  // true if all elements in ruleArray and in answersArray, but answersArray can have more elements
  return ruleArray.every((value) => answersArray.includes(value));
};

const isBroadMatch = (ruleArray, answersArray) => {
  // true if at least one element in ruleArray is in answersArray
  return ruleArray.some((value) => answersArray.includes(value));
};

const checkCondition = (condition, answers) => {
  // find an answer with a key matching the propertyId
  const answer = answers?.[condition.propertyId]?.trim()?.toLowerCase();

  if (condition?.match === "provided") {
    if (condition?.verb === "is" && answer) {
      return true;
    } else if (condition?.verb === "is_not" && !answer) {
      return true;
    } else if (condition?.verb === "is" && !answer) {
      return false;
    } else if (condition?.verb === "is_not" && answer) {
      return false;
    }
  }

  if (!condition?.verb || !condition?.propertyId || !condition?.propertyValue || !condition?.match) {
    return false;
  }

  if (!answer) {
    return condition?.verb === "is" ? false : true;
  }

  const answerArray = answer.split(",")?.map((item) => item.trim().toLowerCase());
  const conditionArray = condition?.propertyValue.split(",")?.map((item) => item.trim().toLowerCase());
  let isMatch = false;
  if (condition?.match === "exactly") {
    isMatch = isExactMatch(conditionArray, answerArray);
  } else if (condition?.match === "at_least") {
    isMatch = isOpenMatch(conditionArray, answerArray);
  } else if (condition?.match === "one_of") {
    isMatch = isBroadMatch(conditionArray, answerArray);
  }

  return condition?.verb === "is" ? isMatch : !isMatch;
};

export const InputSlider = ({ current, question, isLastQuestion }: QuestionFormProps) => {
  const { session, addAnswer, endSession } = useSession();
  const isMobile = useIsMobile();
  const history = useHistory();
  const intl = useIntl();
  const { flow, totalQuestions, hasWelcomeScreen } = useFlow();
  const [isBusy, setBusy] = useState(false);
  const [questionPageLoaded, setQuestionPageLoaded] = useState(false);
  const [canDisplayQuestion, setCanDisplayQuestion] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!session || !question) {
      return;
    }

    const answers = session?.answers ? JSON.parse(session?.answers) : {};
    const pageSettings = JSON.parse(question?.pageSettings ?? "{}");
    const condition_settings = pageSettings?.conditions ? JSON.parse(pageSettings.conditions) : {};

    const must_match_all = condition_settings?.anyAll === "all";
    const should_skip = condition_settings?.showSkip === "skip";
    const conditions = condition_settings?.conditions ?? [];

    if (!conditions || conditions.length === 0) {
      // if no conditions are set, display the question
      setCanDisplayQuestion(true);
      return;
    }

    let conditionsMet = false;
    if (must_match_all) {
      // need to meet all conditions
      conditionsMet = conditions.every((condition) => {
        return checkCondition(condition, answers);
      });
    } else {
      // need to meet at least one condition
      conditionsMet = conditions.some((condition) => {
        return checkCondition(condition, answers);
      });
    }

    const skip = (conditionsMet && should_skip) || (!conditionsMet && !should_skip);

    if (skip) {
      // if this is from the back button, go back even more
      if (history?.action === "POP") {
        history.goBack();
      } else {
        // skip the question
        if (current >= totalQuestions) {
          openResults();
        } else {
          history.push(`/questions/${current + 1}`);
        }
      }
    } else {
      // if the question is not skipped, display it
      setCanDisplayQuestion(true);
    }
  }, [question, session]);

  useEffect(() => {
    if (session?.id && question?.id && !questionPageLoaded) {
      /* advanced analytics */
      // load_question_page
      setQuestionPageLoaded(true);
      if ((window as any)?.lantern_analytics_event) {
        (window as any).lantern_analytics_event({
          event: "load_question_page",
          page: "question",
          question_type: question.type,
          title: question.title,
          session_id: session.id,
          question_id: question.id,
        });
      }
      if ((window as any)?.lantern_gtag) {
        (window as any).lantern_gtag({
          event: "load_question_page",
          page: "question",
          question_type: question.type,
          title: question.title,
          session_id: session.id,
          question_id: question.id,
        });
      }
      /**********************/
    }
  }, [session, question]);

  useEffect(() => {
    if (question?.id) {
      // emit event lantern:display_question
      const displayQuestionEvent = new CustomEvent("lantern:display_question", {
        bubbles: true,
        cancelable: true,
        composed: false,
        detail: {
          id: question.id,
          title: question.title,
          description: question.description,
          type: question.type,
          options: question.options,
          isRequired: question.isRequired,
        },
      });
      window.dispatchEvent(displayQuestionEvent);
    }
  }, [question]);

  useEffect(() => {
    const sendHeightToParent = () => {
      const height = document.body.scrollHeight;
      if (height) {
        window?.parent?.postMessage(
          {
            action: "resize",
            frameHeight: height,
          },
          "*"
        );
      }
    };

    const images = document.querySelectorAll("img");
    images.forEach((image) => {
      if (image.complete) {
        sendHeightToParent();
      } else {
        image.addEventListener("load", sendHeightToParent);
        image.addEventListener("error", sendHeightToParent);
      }
    });
    window.addEventListener("load", sendHeightToParent);

    sendHeightToParent();

    const resizeObserver = new ResizeObserver(sendHeightToParent);
    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
      images.forEach((image) => {
        image.removeEventListener("load", sendHeightToParent);
        image.removeEventListener("error", sendHeightToParent);
        window.removeEventListener("load", sendHeightToParent);
      });
    };
  }, []);

  const openResults = async () => {
    if (isBusy) {
      return;
    }

    setBusy(true);

    const success = await endSession();

    if (success) {
      history.push("/results");
    } else {
      setBusy(false);
    }
  };

  const sessionVariables = session?.answers ? JSON.parse(session?.answers) : {};

  const contentBlocks = useMemo(() => JSON.parse(question?.contentBlocks ?? "{}"), [question]);

  const pageSettings = JSON.parse(question?.pageSettings ?? "{}");

  /* general design settings */
  const topWidthDesktopType = pageSettings?.topWidthDesktopType ?? "100%";
  const topWidth = pageSettings?.topWidth ?? "1200";
  const bottomWidthDesktopType = pageSettings?.bottomWidthDesktopType ?? "100%";
  const bottomWidth = pageSettings?.bottomWidth ?? "1200";
  const topWidthMobileType = pageSettings?.topWidthMobileType ?? "100%";
  const topWidthMobile = pageSettings?.topWidthMobile ?? "1200";
  const bottomWidthMobileType = pageSettings?.bottomWidthMobileType ?? "100%";
  const bottomWidthMobile = pageSettings?.bottomWidthMobile ?? "1200";
  /* button settings */
  const useDefaultColorForButton = pageSettings?.useDefaultColorForButton ?? "yes";
  const buttonColor = pageSettings?.buttonColor ?? "#000";
  const useDefaultTextColorForButton = pageSettings?.useDefaultTextColorForButton ?? "yes";
  const buttonTextColor = pageSettings?.buttonTextColor ?? "#fff";
  const buttonFontSize = pageSettings?.buttonFontSize ?? "18";
  const buttonBorderRadius = pageSettings?.buttonBorderRadius ?? "8";
  const buttonHorizontalPadding = pageSettings?.buttonHorizontalPadding ?? "56";
  const buttonVerticalPadding = pageSettings?.buttonVerticalPadding ?? "20";
  const buttonFontSizeMobile = pageSettings?.buttonFontSizeMobile ?? "18";
  const buttonBorderRadiusMobile = pageSettings?.buttonBorderRadiusMobile ?? "8";
  const buttonHorizontalPaddingMobile = pageSettings?.buttonHorizontalPaddingMobile ?? "56";
  const buttonVerticalPaddingMobile = pageSettings?.buttonVerticalPaddingMobile ?? "20";
  const buttonWidthMobileType = pageSettings?.buttonWidthMobileType ?? "100%";
  const buttonWidthMobile = pageSettings?.buttonWidthMobile ?? "345";
  const buttonWidthType = pageSettings?.buttonWidthType ?? "custom";
  const buttonWidth = pageSettings?.buttonWidth ?? "370";
  const buttonAlignment = pageSettings?.buttonAlignment ?? "center";
  const inputOne_buttonTextOverride = pageSettings?.inputOne_buttonTextOverride ?? "";
  /* question settings */
  const questionFontSize = pageSettings?.questionFontSize ?? "32";
  const questionAlignment = pageSettings?.questionAlignment ?? "center";

  const minValue = pageSettings?.minValue ? parseFloat(pageSettings?.minValue) ?? 0 : 0;
  const maxValue = pageSettings?.maxValue ? parseFloat(pageSettings?.maxValue) ?? 10 : 10;
  const step = pageSettings?.step ? parseFloat(pageSettings?.step <= 0 ? 1 : pageSettings.step) : 1;
  const defaultValue = pageSettings?.defaultValue ?? 5;
  const sliderColor = pageSettings?.sliderColor ?? "#000";

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: {
      customInput: defaultValue <= maxValue && defaultValue >= minValue ? defaultValue : minValue,
    },
  });

  const customInput = useWatch({ name: "customInput", control });

  const minLength = question.minLength ? parseInt(question.minLength) : 0;

  const onSubmit = async (values) =>
    new Promise<void>(async (resolve) => {
      if (question.isRequired && !customInput) {
        showToast(intl.formatMessage(messages.answerRequired), "error");
        resolve();
      }

      if (session?.id && question?.id) {
        /* advanced analytics */
        // submit_answer
        if ((window as any)?.lantern_analytics_event) {
          (window as any).lantern_analytics_event({
            event: "submit_answer",
            page: "question",
            question_type: question.type,
            session_id: session.id,
            question_id: question.id,
            title: question.title,
            is_required: question.isRequired,
            answers: JSON.stringify([customInput]),
            property_id: question.propertyID ?? "",
            options: JSON.stringify(question.options),
          });
        }
        if ((window as any)?.lantern_gtag) {
          (window as any).lantern_gtag({
            event: "submit_answer",
            page: "question",
            question_type: question.type,
            session_id: session.id,
            question_id: question.id,
            title: question.title,
            is_required: question.isRequired,
            answers: JSON.stringify([customInput]),
            property_id: question.propertyID ?? "",
            options: JSON.stringify(question.options),
          });
        }
        // log individual answer
        if ((window as any)?.lantern_analytics_event) {
          (window as any).lantern_analytics_event({
            event: "log_answer",
            page: "question",
            question_type: question.type,
            session_id: session.id,
            question_id: question.id,
            title: question.title,
            is_required: question.isRequired,
            answers: customInput,
            property_id: question.propertyID ?? "",
            options: JSON.stringify(question.options),
          });
        }
        /**********************/
      }

      // emit event lantern:submit_answer
      const submitAnswerEvent = new CustomEvent("lantern:submit_answer", {
        bubbles: true,
        cancelable: true,
        composed: false,
        detail: {
          id: question.id,
          title: question.title,
          description: question.description,
          type: question.type,
          options: question.options,
          isRequired: question.isRequired,
          property_id: question.propertyID ?? "",
          answers: [customInput],
        },
      });
      window.dispatchEvent(submitAnswerEvent);

      const result = await addAnswer({
        nodeId: question.id,
        propertyName: question.propertyID ?? "",
        propertyValue: customInput,
      });

      if (!result) {
        resolve();
        return;
      }

      if ((current === totalQuestions && (!question?.nextNode || question?.nextNode === "auto")) || question?.nextNode === "results") {
        await openResults();
      } else {
        if (!question?.nextNode || question?.nextNode === "auto") {
          history.push(`/questions/${current + 1}`);
        } else {
          const nextQuestionIdx = flow.nodes?.findIndex(({ id }) => id === question.nextNode);
          if (!nextQuestionIdx || nextQuestionIdx === -1) {
            history.push(`/questions/${current + 1}`);
          } else {
            history.push(`/questions/${nextQuestionIdx + (hasWelcomeScreen ? 0 : 1)}`);
          }
        }
      }
    });

  if (!canDisplayQuestion) {
    return null;
  }

  return (
    <form className="question-form" id={question?.id ?? ""} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <m.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={config.transition} className="input-slider input-wrapper">
        <div
          className="input-slider-top-section"
          style={{
            margin: "0 auto",
            width: isMobile ? (topWidthMobileType === "100%" ? "100%" : `${topWidthMobile}px`) : topWidthDesktopType === "100%" ? "100%" : `${topWidth}px`,
          }}
        >
          <QuestionContentBlocksList contentBlocks={contentBlocks?.top} sessionVariables={sessionVariables} />

          <CenteredTitle
            role="heading"
            aria-level={1}
            tabIndex={1}
            aria-live="assertive"
            className="input-slider input-question"
            style={{
              fontSize: `${questionFontSize}px`,
              textAlign: questionAlignment,
              lineHeight: "1.2",
            }}
          >
            {replaceVariables(question?.title, sessionVariables)}
          </CenteredTitle>

          <QuestionContentBlocksList contentBlocks={contentBlocks?.middle} sessionVariables={sessionVariables} />
        </div>
        <div
          className="input-slider-bottom-section"
          style={{
            margin: "0 auto",
            width: isMobile
              ? bottomWidthMobileType === "100%"
                ? "100%"
                : `${bottomWidthMobile}px`
              : bottomWidthDesktopType === "100%"
              ? "100%"
              : `${bottomWidth}px`,
          }}
        >
          <Box className="input-slider input-box-container" width={{ _: "100%" }} mx="auto" my="9">
            <Controller
              name="customInput"
              control={control}
              defaultValue={defaultValue}
              render={({ field }) => (
                <Slider
                  trackStyle={{ backgroundColor: sliderColor, height: 10 }}
                  handleStyle={{
                    borderColor: sliderColor,
                    borderWidth: 5,
                    height: 28,
                    width: 28,
                    backgroundColor: sliderColor,
                    opacity: 1,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0)",
                  }}
                  step={step < maxValue - minValue ? step : 1}
                  min={minValue < maxValue ? minValue : maxValue}
                  max={maxValue > minValue ? maxValue : minValue}
                  defaultValue={defaultValue <= maxValue && defaultValue >= minValue ? defaultValue : minValue}
                  marks={Object.fromEntries(
                    Array.from({ length: Math.ceil((maxValue - minValue) / step) + 1 }, (_, i) => {
                      const v = Math.min(minValue + i * step, maxValue);
                      return [v, String(v)];
                    })
                  )}
                  dotStyle={{
                    color: "transparent",
                    fontSize: "12px",
                    width: "0px",
                    textAlign: "center",
                    height: "0px",
                    lineHeight: "20px",
                    borderRadius: "50%",
                    backgroundColor: "transparent",
                    borderColor: "transparent",
                    borderWidth: "0px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0)",
                  }}
                  railStyle={{ backgroundColor: "lightgray", height: 10 }}
                  className="input-slider input-box"
                  {...field}
                  onChange={(v) => field.onChange(String(v))}
                />
              )}
            />
          </Box>

          <QuestionContentBlocksList contentBlocks={contentBlocks?.bottom} sessionVariables={sessionVariables} />

          <m.div
            className="input-slider input-button-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, ...config.transition }}
            style={{
              display: "flex",
              justifyContent: buttonAlignment === "center" ? "center" : buttonAlignment === "right" ? "flex-end" : "flex-start",
            }}
          >
            <Button
              className="input-slider input-button button"
              arrow
              width={
                isMobile
                  ? buttonWidthMobileType === "100%"
                    ? "100%"
                    : buttonWidthMobileType === "auto"
                    ? "auto"
                    : `${buttonWidthMobile}px`
                  : buttonWidthType === "100%"
                  ? "100%"
                  : buttonWidthType === "auto"
                  ? "auto"
                  : `${buttonWidth}px`
              }
              type="submit"
              isLoading={isSubmitting}
              disabled={(customInput.length === 0 && question.isRequired) || (question.isRequired && minLength > 0 && customInput?.length < minLength)}
              backgroundColor={useDefaultColorForButton === "no" ? buttonColor : undefined}
              textColor={useDefaultTextColorForButton === "no" ? buttonTextColor : undefined}
              style={
                isMobile
                  ? {
                      ...(buttonFontSizeMobile >= 0
                        ? {
                            fontSize: `${buttonFontSizeMobile}px`,
                          }
                        : {}),
                      ...(buttonBorderRadiusMobile >= 0
                        ? {
                            borderRadius: `${buttonBorderRadiusMobile}px`,
                          }
                        : {}),
                      ...(buttonHorizontalPaddingMobile >= 0
                        ? {
                            paddingLeft: `${buttonHorizontalPaddingMobile}px`,
                            paddingRight: `${buttonHorizontalPaddingMobile}px`,
                          }
                        : {}),
                      ...(buttonVerticalPaddingMobile >= 0
                        ? {
                            paddingTop: `${buttonVerticalPaddingMobile}px`,
                            paddingBottom: `${buttonVerticalPaddingMobile}px`,
                          }
                        : {}),
                    }
                  : {
                      ...(buttonFontSize >= 0 ? { fontSize: `${buttonFontSize}px` } : {}),
                      ...(buttonBorderRadius >= 0
                        ? {
                            borderRadius: `${buttonBorderRadius}px`,
                          }
                        : {}),
                      ...(buttonHorizontalPadding >= 0
                        ? {
                            paddingLeft: `${buttonHorizontalPadding}px`,
                            paddingRight: `${buttonHorizontalPadding}px`,
                          }
                        : {}),
                      ...(buttonVerticalPadding >= 0
                        ? {
                            paddingTop: `${buttonVerticalPadding}px`,
                            paddingBottom: `${buttonVerticalPadding}px`,
                          }
                        : {}),
                    }
              }
            >
              {inputOne_buttonTextOverride?.length > 0 ? inputOne_buttonTextOverride : <FormattedMessage {...messages[isLastQuestion ? "finish" : "next"]} />}
            </Button>
          </m.div>
        </div>
      </m.div>
    </form>
  );
};
